import React, { useState, useEffect } from "react";
import '../../css/PermissionRequestForm.css';
import { useTranslation } from 'react-i18next';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import { isMobile } from 'react-device-detect';
import { useLoading } from '../../LoadingContext';
import DateInput from '../common/DateInput';
import TimeInput from '../common/TimeInput';

registerPlugin(FilePondPluginImagePreview, FilePondPluginFileValidateType, FilePondPluginFileValidateSize);
const loadingGif = process.env.REACT_APP_PUBLIC_URL + '/loading1.gif';

function PermissionRequestForm({ language, permissionRequestData, isFormDisabled: isFormDisabledProp, onSendMessage }) {
  const [formData, setFormData] = useState({
    permissionType: permissionRequestData.permission_type,
    date: permissionRequestData.date,
    endDate: permissionRequestData.end_date,
    startTime: permissionRequestData.start_time,
    endTime: permissionRequestData.end_time,
    duration: permissionRequestData.duration,
    notes: "",
    attachments: []
  });

  const [calculatedDuration, setCalculatedDuration] = useState('');
  const [permissionTypes, setPermissionTypes] = useState([]);
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});
  const [responseMessage, setResponseMessage] = useState("");
  const [isFormDisabled, setIsFormDisabled] = useState(isFormDisabledProp);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(true);
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const [isMultipleDays, setIsMultipleDays] = useState(formData.duration === '033');
  const [csrfToken, setCSRFToken] = useState("");
  const [cookies, setCookies] = useState("");
  const [loadingCounter, setLoadingCounter] = useState(2); // Start with the number of async operations
  const [validFiles, setValidFiles] = useState(true);
  const [attachmentFilesTypes, setAttachmentFilesTypes] = useState([
    "application/pdf",
    "image/jpeg",
    "image/tiff",
    "image/gif",
    "image/jpeg",
    "image/tiff"
  ]);
  const { setIsAPILoading } = useLoading();
  const [workScheduleData, setWorkScheduleData] = useState();
  const [isPrivateSchool, setIsPrivateSchool] = useState(false);

  useEffect(() => {
    if (formData.duration === '033') {
      setIsMultipleDays(true)
    }
  }, [formData.duration])
  const decrementLoadingCounter = () => {
    setLoadingCounter(prevCount => {
      const newCount = prevCount - 1;
      if (newCount === 0) {
        setIsFormLoading(false);  // Only set loading to false when all async operations are done
      }
      return newCount;
    });
  };

  function convertToCookieString(jsonObject) {
    const cookieArray = [];
    for (const [key, value] of Object.entries(jsonObject)) {
      cookieArray.push(`${key}=${value}`);
    }
    return cookieArray.join('; ');
  }

  // Get CSRF token and Cookies
  useEffect(() => {
    const fetchData = async () => {
      try {
        let frontendLog = JSON.stringify({ "sessionID": sessionStorage.getItem('sessionID'), "EmployeeID": localStorage.getItem('EmployeeID'), "channel": isMobile ? "Mobile" : "Portal", "language": localStorage.getItem('language') })
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/middleware/getCurrentEmployee?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}&token=${encodeURIComponent(localStorage.getItem('id_token'))}&sessionID=${encodeURIComponent(sessionStorage.getItem('sessionID'))}&frontendLog=${encodeURIComponent(frontendLog)}`);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const result = await response.json();
        setCSRFToken(result.csrfToken);
        localStorage.setItem('EmployeeID', result.EmployeeID)
        setCookies(convertToCookieString(result.cookies));
      } catch (error) {
        console.error(error);
      } finally {
        decrementLoadingCounter()
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let frontendLog = JSON.stringify({ "sessionID": sessionStorage.getItem('sessionID'), "EmployeeID": localStorage.getItem('EmployeeID'), "channel": isMobile ? "Mobile" : "Portal", "language": localStorage.getItem('language') })
    const params = new URLSearchParams({ "assertion": localStorage.getItem('id_token'), "language": language, "sessionID": sessionStorage.getItem('sessionID'), "frontendLog": frontendLog });
    fetch(`${process.env.REACT_APP_BACKEND_URL}/middleware/getPermissionTypes?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}&${params.toString()}`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        setPermissionTypes(data);
      })
      .catch(error => {
        console.error('Error fetching permission types:', error);
      }).finally(e => {
        decrementLoadingCounter()
      });
  }, [language]);

  const isWorkFromHome = formData.permissionType === '0680' || formData.permissionType === '0695';

  const [isElgQaMother, setIsElgQaMother] = useState(false);

  useEffect(() => {
    const selectedPermission = permissionTypes.find(type => type.Awart === formData.permissionType);
    if (selectedPermission) {
      setIsElgQaMother(selectedPermission.IsElgQaMother);
    }
  }, [formData.permissionType, permissionTypes]);

  useEffect(() => {
    if (isWorkFromHome ) {
      if (isElgQaMother) {
        // Enable both start and end date, both mandatory
        setErrors((prevErrors) => ({ ...prevErrors, date: "", endDate: "" }));
      } else {
        // Enable start date, disable end date and set it to 7 days after start date
        setFormData((prevFormData) => ({
          ...prevFormData,
          duration: '033',
          endDate: calculateEndDate(prevFormData.date, 6),
        }));
      }
      // Disable time fields
      setFormData((prevFormData) => ({
        ...prevFormData,
        startTime: "",
        endTime: "",
        endDate: calculateEndDate(prevFormData.date, 6),
      }));
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        duration: "022",
        date: permissionRequestData.date,
        endDate: permissionRequestData.end_date,
        startTime: permissionRequestData.start_time,
        endTime: permissionRequestData.end_time
      }));
      setIsMultipleDays(false)
    }
  }, [formData.permissionType, isElgQaMother]);

  useEffect(() => {
    if (isWorkFromHome && formData.date) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        endDate: calculateEndDate(prevFormData.date, 6), // Set endDate to 6 days after the start date
      }));
    }
  }, [formData.date, isWorkFromHome ]);

  const calculateEndDate = (startDate, daysToAdd) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + daysToAdd);
    return date.toISOString().split("T")[0];
  };

  useEffect(() => {
    setIsFormDisabled(isFormDisabledProp);
  }, [isFormDisabledProp]);

  const calculateDuration = (startTime, endTime) => {
    const start = new Date(`1970-01-01T${startTime}Z`);
    const end = new Date(`1970-01-01T${endTime}Z`);
    const diffMs = end - start;

    const diffHrs = Math.floor((diffMs % 86400000) / 3600000);
    const diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000);

    const diffMinsInHours = diffMins / 60;
    const totalHours = diffHrs + diffMinsInHours;

    const formattedHours = Number.isInteger(totalHours) ? totalHours : totalHours.toFixed(1);

    return `${formattedHours} ${t('hours')}`;
  };

  useEffect(() => {
    const runEffect = async () => {
      if (formData.startTime && formData.endTime) {
        const duration = calculateDuration(formData.startTime, formData.endTime);
        setCalculatedDuration(duration);
      }

      const startTimeForCalculation = !isWorkFromHome ? formatTimeForCalculation(formData.startTime) : "";
      const endTimeForCalculation = !isWorkFromHome ? formatTimeForCalculation(formData.endTime) : "";
      const startDate = formatDate(formData.date);
      // Make API Call to get the remaining hours
      if(!isWorkFromHome ){
        await getWorkSchedule(
          formData.permissionType,
          startDate,
          formData.endDate,
          startTimeForCalculation,
          endTimeForCalculation
        );
      }
    };

    runEffect();
  }, [formData.startTime, formData.endTime]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value
    }));
  };

  const handleFilePondUpdate = (fileItems) => {
    const files = fileItems.map(fileItem => fileItem.file);

    // Check if all files are of accepted types
    const allValid = files.every(file => attachmentFilesTypes.includes(file.type));
    setValidFiles(allValid);

    setFormData((prevFormData) => ({
      ...prevFormData,
      attachments: files
    }));

    // Set error if any invalid file is uploaded
    if (!allValid) {
      setErrors(prevErrors => ({
        ...prevErrors,
        attachments: t("One or more files are not in the correct format.")
      }));
    } else {
      setErrors(prevErrors => ({ ...prevErrors, attachments: "" }));
    }
  };

  const handleDurationChange = (e) => {
    const { value } = e.target;
    setIsMultipleDays(value === '033');
    setFormData((prevFormData) => ({
      ...prevFormData,
      duration: value
    }));
  };

  const validate = () => {
    let tempErrors = {};
    if (!formData.startTime && !isWorkFromHome ) tempErrors.startTime = t("Start Time is required")
    if (!formData.endTime && !isWorkFromHome ) tempErrors.endTime = t("End Time is required")
    if (!formData.date) tempErrors.date = t("Date is required.");
    if (isMultipleDays && !formData.endDate) tempErrors.endDate = t("End date is required.");
    if (isMultipleDays && new Date(formData.date) > new Date(formData.endDate))
      tempErrors.endDate = t("Start date cannot be later than end date.");
    if (!validFiles) {
      tempErrors.attachments = t("Invalid file type. Please upload PDF, JPEG, TIFF, or GIF files.");
    }
    if (formData.permissionType === '0695' && formData.attachments.length === 0) {
      tempErrors.attachments = t("Attachment is required");
    }
    if (formData.permissionType === "0660") {
      // Always require 2 attachments
      const requiredCount = isPrivateSchool ? 3 : 2;
      if (formData.attachments.length < requiredCount) {
        let missingFiles = [];
        if (formData.attachments.length < 1) missingFiles.push(t("Birth Certificate of the Child"));
        if (formData.attachments.length < 2) missingFiles.push(t("School Enrollment Certificate"));
        if (isPrivateSchool && formData.attachments.length < 3) missingFiles.push(t("School Calendar"));
        tempErrors.attachments = t("Required documents:") + missingFiles.join("، ");
      }
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const isoString = d.toISOString();
    const formatted = isoString.substring(0, 19);
    return formatted;
  };

  // for calculation
  const formatTimeForCalculation = (time) => {
    const [hours, minutes, seconds] = time.split(':').map(Number);
    const formatted = `PT${String(hours).padStart(2, '0')}H${String(minutes).padStart(2, '0')}M${String(seconds || 0).padStart(2, '0')}S`;
    return formatted;
  };

  // for permission request
  const formatTimeForRequest = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const formatted = `${String(hours).padStart(2, '0')}${String(minutes).padStart(2, '0')}00`;
    return formatted;
  };

  // for remaining hours
  function formatTimeForRemainingHours(timeStr) {
    if (!timeStr || timeStr.length < 6) return "";
  
    // Extract hours and minutes from HHMMSS format
    const hours = parseInt(timeStr.substring(0, 2), 10);
    const minutes = parseInt(timeStr.substring(2, 4), 10);
  
    // Convert minutes to fractional hours
    const totalHours = hours + minutes / 60;
  
    // Round to one decimal if needed
    return totalHours.toFixed(1) + " " + t('hours');
  }
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setResponseMessage(true);

    try {
      const startDate = formatDate(formData.date);
      const endDate = isMultipleDays ? formatDate(formData.endDate) : startDate;
      const startTimeForCalculation = !isWorkFromHome ? formatTimeForCalculation(formData.startTime) : "";
      const endTimeForCalculation = !isWorkFromHome ? formatTimeForCalculation(formData.endTime) : "";
      const startTimeForRequest = !isWorkFromHome ? formatTimeForRequest(formData.startTime) : "";
      const endTimeForRequest = !isWorkFromHome ? formatTimeForRequest(formData.endTime) : "";

      const selectedPermissionType = permissionTypes.find(type => type.Awart === formData.permissionType);
      const leaveName = selectedPermissionType ? selectedPermissionType.Atext : "";
      let frontendLog = JSON.stringify({ "sessionID": sessionStorage.getItem('sessionID'), "EmployeeID": localStorage.getItem('EmployeeID'), "channel": isMobile ? "Mobile" : "Portal", "language": localStorage.getItem('language') })

      const params = new URLSearchParams({
        "assertion": localStorage.getItem('id_token'),
        "startDate": startDate,
        "endDate": endDate,
        "startTime": startTimeForCalculation,
        "endTime": endTimeForCalculation,
        "permissionCode": formData.permissionType,
        "language": language,
        "sessionID": sessionStorage.getItem('sessionID'),
        "frontendLog": frontendLog
      });

      const privateSchoolValue = formData.permissionType === "0660" ? (isPrivateSchool ? "1" : "0") : "0";
      params.append("PrivateSchool", privateSchoolValue);

      setIsAPILoading(true);

      if (!isWorkFromHome ) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/middleware/calculateAbsenceHours?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}&${params.toString()}`);
        if (!response.ok) {
          if (response.status === 400 || response.status === 404) {
            const errorData = await response.json();
            setIsFormDisabled(false)
            setIsLoading(false)
            // handleErrorResponse(response);
            setIsErrorMessage(true);
            setResponseMessage(errorData.error.message.value)
            return;
          }
          setIsFormDisabled(false)
          setIsLoading(false)
          setIsErrorMessage(true);
          setResponseMessage(response.status)
        }

        const data = await response.json();
        if (data.Code !== "200") {
          setIsErrorMessage(true);
          setIsLoading(false)
          setIsFormDisabled(false)
          setResponseMessage(data.Message ?? t("There was an error occurred, Please try again later."));
          return;
        } else {
          setIsErrorMessage(false);
          setResponseMessage(data.Message)
          setIsAPILoading(false);
        }
      }

      const isWorkScheduleValid = await getWorkSchedule(formData.permissionType, startDate, endDate, startTimeForCalculation, endTimeForCalculation);
      if (!isWorkScheduleValid) {
        return; // Do not proceed with the request if work schedule is not valid
      }

      await createPermissionRequest(startDate, endDate, startTimeForRequest, endTimeForRequest, leaveName);

    } catch (error) {
      console.error('Error during submission:', error);
      setIsLoading(false);
      setIsFormDisabled(false)
      setIsErrorMessage(true);
      setIsAPILoading(false);
    }
  };

  const getWorkSchedule = async (permissionCode, startDate, endDate, startTime, endTime) => {
    try {
      if(isErrorMessage){
        setResponseMessage("")
      }
      let frontendLog = JSON.stringify({ "sessionID": sessionStorage.getItem('sessionID'), "EmployeeID": localStorage.getItem('EmployeeID'), "channel": isMobile ? "Mobile" : "Portal", "language": localStorage.getItem('language') })
      const params = new URLSearchParams({
        permissionCode,
        startDate,
        endDate,
        startTime,
        endTime,
        "sessionID": sessionStorage.getItem('sessionID'),
        "assertion": localStorage.getItem('id_token'),
        frontendLog: frontendLog,
        language: localStorage.getItem('language')
      });
      setIsAPILoading(true);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/middleware/workSchedule?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}&${params.toString()}`);

      if (!response.ok) {
        setIsFormDisabled(false)
        setIsLoading(false)
        setIsErrorMessage(true);
        setResponseMessage(response.status)
        if(response.status == 400)
        {
          const errorData = await response.json();
          console.log({errorData})
          setResponseMessage(errorData.error.message.value)
          setIsErrorMessage(true)
          return;
        }
        setIsAPILoading(false);
        throw new Error('Error fetching work schedule');
      }

      const workScheduleData = await response.json();
      setWorkScheduleData(workScheduleData)

      // Check if Beguz or Enduz are PT00H00M00S
      if (workScheduleData.Beguz === "PT00H00M00S" || workScheduleData.Enduz === "PT00H00M00S") {
        setIsErrorMessage(true);
        setResponseMessage(t("Permission time is not according to your work schedule"));
        setIsLoading(false);
        setIsFormDisabled(false);
        setIsAPILoading(false);
        return false; // Return false to prevent further execution
      }

      return true; // Work schedule is valid

    } catch (error) {
      console.error('Error fetching work schedule:', error);
      setIsErrorMessage(true);
      setResponseMessage("Error fetching work schedule.");
      setIsLoading(false);
      setIsFormDisabled(false);
      setIsAPILoading(false);
      return false; // Return false to prevent further execution
    }
  };

  const handleErrorResponse = async (response) => {
    setIsLoading(false);
    setIsAPILoading(false);

    if (response.status === 400) {
      const errorData = await response.json();
      setIsErrorMessage(true);
      setResponseMessage(errorData.errorData[0]);
    } else {
      setIsFormDisabled(false)
      setIsLoading(false)
      setIsErrorMessage(true);
      setResponseMessage(response.status)
      throw new Error('An error occurred: ' + response.statusText);
    }
  };

  const createPermissionRequest = async (startDate, endDate, startTimeForRequest, endTimeForRequest, leaveName) => {
    setIsFormDisabled(true);
    setIsErrorMessage(false);
    let frontendLog = JSON.stringify({ "sessionID": sessionStorage.getItem('sessionID'), "EmployeeID": localStorage.getItem('EmployeeID'), "channel": isMobile ? "Mobile" : "Portal", "language": localStorage.getItem('language') })

    setIsAPILoading(true);

    const createPermissionRequestEndpoint = `${process.env.REACT_APP_BACKEND_URL}/middleware/createPermissionRequest?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}`;
    const params = {
      "assertion": localStorage.getItem('id_token'),
      "x-csrf-token": csrfToken,
      "cookies": cookies,
      "AbsenceTypeCode": formData.permissionType,
      "StartDate": startDate,
      "EndDate": endDate,
      "StartTime": startTimeForRequest,
      "EndTime": endTimeForRequest,
      "Notes": formData.notes ? formData.notes : "",
      "EmployeeID": localStorage.getItem('EmployeeID'),
      "AbsenceTypeName": leaveName,
      "language": language,
      "sessionID": sessionStorage.getItem('sessionID'),
      "frontendLog": frontendLog,
      "PrivateSchool": isPrivateSchool ? "X" : ""
    };

    const queryString = new URLSearchParams(params).toString();
    const urlWithParams = `${createPermissionRequestEndpoint}&${queryString}`;

    try {
      const response = await fetch(urlWithParams, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (!response.ok) {
        setIsFormDisabled(false);
        handleErrorResponse(response);
        return;
      }

      const anotherData = await response.json();
      await handleSuccessfulPermissionRequest(anotherData);
      setIsAPILoading(false);

    } catch (error) {
      console.error('Error during permission request:', error);
      setIsFormDisabled(false)
      setIsLoading(false);
      setIsErrorMessage(true);
      setResponseMessage(t("Failed to create permission request"))
      setIsAPILoading(false);
    }
  };

  const handleSuccessfulPermissionRequest = async (anotherData) => {
    let frontendLog = { "sessionID": sessionStorage.getItem('sessionID'), "EmployeeID": localStorage.getItem('EmployeeID'), "channel": isMobile ? "Mobile" : "Portal", "language": localStorage.getItem('language') }
    const requestData = {
      message: t("permission request saved successfully, you need any help?"),
      user_lang: language,
      session_id: sessionStorage.getItem('sessionID'),
      frontendLog: frontendLog
    };

    const { RequestID, LeaveKey } = anotherData;

    try {
      if (formData.attachments.length > 0) {
        await uploadAttachments(RequestID, LeaveKey);
      }
      setIsAPILoading(true);

      await cancelPermissionRequest(requestData);
      setIsLoading(false);
      setIsAPILoading(false);

    } catch (error) {
      console.error('Error handling successful permission request:', error);
      setIsAPILoading(false);
    }
  };

  const uploadAttachments = async (RequestID, LeaveKey) => {
    let frontendLog = JSON.stringify({
      "sessionID": sessionStorage.getItem('sessionID'),
      "EmployeeID": localStorage.getItem('EmployeeID'),
      "channel": isMobile ? "Mobile" : "Portal",
      "language": localStorage.getItem('language')
    });

    const uploadParams = new URLSearchParams({
      "token": localStorage.getItem('id_token'),
      EmployeeID: localStorage.getItem('EmployeeID'),
      RequestID: RequestID,
      LeaveKey: LeaveKey,
      "x-csrf-token": csrfToken,
      "cookies": cookies,
      "sessionID": sessionStorage.getItem('sessionID'),
      "frontendLog": frontendLog
    }).toString();

    // Iterate over each file and send the binary data directly
    for (const file of formData.attachments) {
      const arrayBuffer = await file.arrayBuffer(); // Convert file to binary ArrayBuffer

      const uploadResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/middleware/sendAttachments?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}&${uploadParams}`, {
        method: 'POST',
        headers: {
          'Content-Type': file.type, // Set appropriate content type (e.g., image/jpeg, application/pdf)
          // 'Slug': file.name, // Send the file name in the Slug header
          'Slug': encodeURIComponent(file.name), // Encode to make it ASCII-safe
        },
        body: arrayBuffer, // Send binary data as the request body
      });

      if (!uploadResponse.ok) {
        setIsFormDisabled(false)
        setIsLoading(false);
        setIsErrorMessage(true);
        setResponseMessage(t("Failed to create permission request"))
        throw new Error('Error uploading attachment: ' + uploadResponse.statusText);
      }

      const uploadData = await uploadResponse.json();
    }
  };

  const cancelPermissionRequest = async (requestData) => {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/chatbot/rephraseMessage?code=${process.env.REACT_APP_CHATBOT_FUNCTION_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();
    onSendMessage({ sender: 'bot', text: data.message, clearHistory: data.clearHistory });
    setTimeout(() => {
      onSendMessage({
        sender: 'bot',
        isRating: true,
        requestType: data.requestType,
        timestamp: new Date().getTime(),
      });
    }, (process.env.REACT_APP_TIME_TO_DISPLAY_REVIEW * 1000));
    sessionStorage.setItem('clearHistory', true)
  };

  const handleCancel = () => {
    setIsLoading(true);
    setResponseMessage(true);
    setIsFormDisabled(true);
    let frontendLog = { "sessionID": sessionStorage.getItem('sessionID'), "EmployeeID": localStorage.getItem('EmployeeID'), "channel": isMobile ? "Mobile" : "Portal", "language": localStorage.getItem('language') }

    const requestData = {
      message: t("permission request cancelled, you need any help?"),
      user_lang: language,
      session_id: sessionStorage.getItem('sessionID'),
      frontendLog: frontendLog
    };

    fetch(`${process.env.REACT_APP_BACKEND_URL}/chatbot/rephraseMessage?code=${process.env.REACT_APP_CHATBOT_FUNCTION_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestData)
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        onSendMessage({ sender: 'bot', text: data.message, clearHistory: data.clearHistory });
        setTimeout(() => {
          onSendMessage({
            sender: 'bot',
            isRating: true,
            requestType: data.requestType,
            timestamp: new Date().getTime(),
          });
        }, (process.env.REACT_APP_TIME_TO_DISPLAY_REVIEW * 1000));
        sessionStorage.setItem('clearHistory', true)
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error making API request:', error);
      });
  };

  return (
    <>
      {isFormLoading ? (
        <div className='centered-v-h'>
          {/* <img src={`${process.env.REACT_APP_PUBLIC_URL}/loading1.gif`} alt="Loading..." className="loading-gif" /> */}
          <img src={loadingGif} alt="Loading..." className="loading-gif" width={50} height={50} />
        </div>
      ) : (
        <>
          <div className="leave-type-select">
            <label className="form-label">{t('Permission Type')}</label>
            <select
              name="permissionType"
              value={formData.permissionType}
              className="form-control small-font"
              onChange={handleChange}
              disabled={isFormDisabled}
            >
              {permissionTypes.map((type) => (
                <option key={type.Awart} value={type.Awart}>
                  {type.Atext}
                </option>
              ))}
            </select>
          </div>
          {formData.permissionType === "0660" && (
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="privateSchool"
                checked={isPrivateSchool}
                onChange={(e) => setIsPrivateSchool(e.target.checked)}
                disabled={isFormDisabled}
              />
              <label className="form-check-label" htmlFor="privateSchool">
                {t("Private School Student.")}
              </label>
            </div>
          )}
          <div className="form-card">
            <div className="form-card-title">
              {t('When will you be take permission?')}
            </div>
            <form onSubmit={handleSubmit} className="w-100">
              {isLoading &&
                <div className='centered-v-h loading-overlay'>
                  <img className="img-bottom" src={loadingGif} alt="Loading..." width={70} height={70} />
                  <span className="span-bottom">{t('Processing, please wait ...')}</span>
                </div>
              }
              <div className="mb-3">
                <label className="form-label">{t('Request Duration')}</label>
                <select
                  name="duration"
                  className="form-control small-font"
                  onChange={handleDurationChange}
                  value={formData.duration}
                  disabled={isWorkFromHome || isFormDisabled}
                >
                  <option value="022">{t('One Day')}</option>
                  <option value="033">{t('More than One Day')}</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">{t('Date')}</label>
                <DateInput
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  disabled={isFormDisabled}
                  placeholder={t('Select date')}
                />
                {errors.date && <span className="error">{errors.date}</span>}
              </div>
              {isMultipleDays && (
                <div className="mb-3">
                  <label className="form-label">{t('End Date')}</label>
                  <DateInput
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    disabled={(formData.permissionType == "0680" && !isElgQaMother) || isFormDisabled}
                    placeholder={t('Select end date')}
                  />
                  {errors.endDate && <span className="error">{errors.endDate}</span>}
                </div>
              )}
              {!isWorkFromHome && (
                <>
                  <div className="mb-3">
                    <label className="form-label">{t('From Time')}</label>
                    <TimeInput
                      name="startTime"
                      value={formData.startTime}
                      onChange={handleChange}
                      disabled={isFormDisabled}
                      placeholder={t('Select start time')}
                    />
                  </div>
                  {errors.startTime && <span className="error">{errors.startTime}</span>}
                  <div className="mb-3">
                    <label className="form-label">{t('End Time')}</label>
                    <TimeInput
                      name="endTime"
                      value={formData.endTime}
                      onChange={handleChange}
                      disabled={isFormDisabled}
                      placeholder={t('Select end time')}
                    />
                  </div>
                  {errors.endTime && <span className="error">{errors.endTime}</span>}
                </>)}
              {!isWorkFromHome &&
                <>
                  <div className="mb-3">
                    <label className="form-label">{t('Calculated Duration')}</label>
                    <div className="num-of-days">
                      {calculatedDuration}
                    </div>
                  </div>
                  {/* Remaining permission hours */}
                  <div className="mb-3">
                    <label className="form-label">{t('Monthly Permission Balance Hours')}</label>
                    <div className="num-of-days">
                      {formatTimeForRemainingHours(workScheduleData?.TOT_P_HRS)}
                    </div>
                  </div>
                </>
              }
              <div className="mb-3">
                <label className="form-label">{t('Notes')}</label>
                <textarea
                  name="notes"
                  className="form-control small-font"
                  value={formData.notes}
                  onChange={handleChange}
                  disabled={isFormDisabled}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">{t('Attachments')}</label>
                <FilePond
                  files={formData.attachments}
                  onupdatefiles={handleFilePondUpdate}
                  allowMultiple={true}
                  maxFiles={10}
                  name="attachments"
                  disabled={isFormDisabled}
                  acceptedFileTypes={['image/jpeg', 'image/tiff', 'image/gif', 'application/pdf']}
                  labelIdle={"<span class='large-font'> + </span>" + t('Drag & Drop your files or') + ' <span class="filepond--label-action"> ' + t('Browse') + ' </span>'}
                />
                {errors.attachments && <span className="error">{errors.attachments}</span>}
              </div>
              <div className="button-group">
                <button className="submit-button" type="submit" disabled={isFormDisabled || isLoading}>
                  {t('Save')}
                </button>
                <button className="cancel-button" type="button" onClick={handleCancel} onTouchStart={handleCancel} disabled={isFormDisabled || isLoading}>
                  {t('Cancel')}
                </button>
              </div>
              {responseMessage && <span className={isErrorMessage ? 'red-span' : 'green-span'}>{responseMessage}</span>}
            </form>
          </div>
        </>
      )}
    </>
  );
}

export default PermissionRequestForm;
